/**
 * `<pg-kanban>` — quadro Kanban reutilizável, com arraste nativo
 * (HTML5 Drag and Drop API, sem dependência externa —
 * `ARCHITECTURE_GUIDE.md`, seção 8). Projetado para servir mais de um
 * contexto (Produção/OPS hoje, tickets de Suporte futuramente —
 * `GLOSSARY.md`, "Kanban") — nunca conhece o significado de negócio de
 * uma coluna ou de um cartão.
 *
 * Dados via PROPRIEDADES JS (mesmo padrão de `pg-table`): `columns` e
 * `cards`.
 *
 * Uso:
 * ```js
 * const kanban = document.querySelector('pg-kanban');
 * kanban.columns = [{ key: 'recebido', label: 'Recebido' }, { key: 'pintura', label: 'Pintura' }];
 * kanban.cards = [{ id: '1', columnKey: 'recebido', titulo: 'OPS #a0f632d9', subtitulo: 'sem técnico' }];
 * ```
 *
 * Eventos emitidos:
 * - `pg-card-move`: arraste de um cartão para outra coluna — `detail: { cardId, deColuna, paraColuna }`.
 *   O componente NUNCA move o cartão sozinho (mesma filosofia de `pg-modal` para `open`) — quem decide
 *   se o movimento é válido e atualiza `cards` de volta é a tela que consome o componente.
 * - `pg-card-click`: clique simples (não arraste) em um cartão — `detail: { cardId }`.
 */
export class PgKanban extends HTMLElement {
    root;
    _columns = [];
    _cards = [];
    colunaArrastando = null;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get columns() {
        return this._columns;
    }
    set columns(valor) {
        this._columns = valor;
        this.render();
    }
    get cards() {
        return this._cards;
    }
    set cards(valor) {
        this._cards = valor;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    render() {
        const colunasHtml = this._columns
            .map((coluna) => {
            const cartoesDaColuna = this._cards.filter((c) => c.columnKey === coluna.key);
            const cartoesHtml = cartoesDaColuna
                .map((cartao) => `
              <div class="cartao" part="cartao" draggable="true" data-card-id="${escaparHtml(cartao.id)}">
                <div class="cartao-titulo">${escaparHtml(cartao.titulo)}</div>
                ${cartao.subtitulo ? `<div class="cartao-subtitulo">${escaparHtml(cartao.subtitulo)}</div>` : ''}
                ${cartao.badgeTexto ? `<pg-badge tone="${cartao.badgeTone ?? 'neutro'}">${escaparHtml(cartao.badgeTexto)}</pg-badge>` : ''}
              </div>`)
                .join('');
            return `
          <div class="coluna" part="coluna" data-column-key="${escaparHtml(coluna.key)}">
            <div class="coluna-cabecalho">
              <span>${escaparHtml(coluna.label)}</span>
              <span class="contagem">${cartoesDaColuna.length}</span>
            </div>
            <div class="coluna-corpo" data-column-key="${escaparHtml(coluna.key)}">${cartoesHtml}</div>
          </div>`;
        })
            .join('');
        this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .quadro {
          display: flex;
          gap: var(--pg-space-4, 16px);
          overflow-x: auto;
          padding-bottom: var(--pg-space-2, 8px);
        }
        .coluna {
          flex: 0 0 260px;
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          display: flex;
          flex-direction: column;
          max-height: 70vh;
        }
        .coluna.sobre-arraste {
          border-color: var(--pg-color-destaque, #2f8cff);
        }
        .coluna-cabecalho {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--pg-space-4, 16px);
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-semibold, 600);
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--pg-color-texto-secundario, #9aa1ab);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
        }
        .contagem {
          background: var(--pg-color-bg-base, #121417);
          border-radius: var(--pg-radius-full, 9999px);
          padding: 1px var(--pg-space-2, 8px);
        }
        .coluna-corpo {
          display: flex;
          flex-direction: column;
          gap: var(--pg-space-3, 12px);
          padding: var(--pg-space-3, 12px);
          overflow-y: auto;
        }
        .cartao {
          background: var(--pg-color-bg-base, #121417);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          padding: var(--pg-space-3, 12px);
          cursor: grab;
          display: flex;
          flex-direction: column;
          gap: var(--pg-space-2, 8px);
        }
        .cartao:hover { border-color: var(--pg-color-destaque, #2f8cff); }
        .cartao.arrastando { opacity: 0.4; }
        .cartao-titulo {
          font-size: var(--pg-font-size-sm, 0.875rem);
          font-weight: var(--pg-font-weight-medio, 500);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        .cartao-subtitulo {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
        }
      </style>
      <div class="quadro" part="quadro">${colunasHtml}</div>
    `;
        this.ligarEventos();
    }
    ligarEventos() {
        this.root.querySelectorAll('.cartao').forEach((cartaoEl) => {
            cartaoEl.addEventListener('dragstart', (evento) => {
                const cardId = cartaoEl.dataset.cardId;
                const cartao = this._cards.find((c) => c.id === cardId);
                this.colunaArrastando = cartao?.columnKey ?? null;
                cartaoEl.classList.add('arrastando');
                evento.dataTransfer?.setData('text/plain', cardId);
                if (evento.dataTransfer)
                    evento.dataTransfer.effectAllowed = 'move';
            });
            cartaoEl.addEventListener('dragend', () => {
                cartaoEl.classList.remove('arrastando');
            });
            cartaoEl.addEventListener('click', () => {
                const cardId = cartaoEl.dataset.cardId;
                this.dispatchEvent(new CustomEvent('pg-card-click', { bubbles: true, composed: true, detail: { cardId } }));
            });
        });
        this.root.querySelectorAll('.coluna-corpo').forEach((corpoEl) => {
            corpoEl.addEventListener('dragover', (evento) => {
                evento.preventDefault();
                if (evento.dataTransfer)
                    evento.dataTransfer.dropEffect = 'move';
                corpoEl.closest('.coluna')?.classList.add('sobre-arraste');
            });
            corpoEl.addEventListener('dragleave', () => {
                corpoEl.closest('.coluna')?.classList.remove('sobre-arraste');
            });
            corpoEl.addEventListener('drop', (evento) => {
                evento.preventDefault();
                corpoEl.closest('.coluna')?.classList.remove('sobre-arraste');
                const cardId = evento.dataTransfer?.getData('text/plain');
                const paraColuna = corpoEl.dataset.columnKey;
                const deColuna = this.colunaArrastando;
                this.colunaArrastando = null;
                if (!cardId || !paraColuna || !deColuna || deColuna === paraColuna)
                    return;
                this.dispatchEvent(new CustomEvent('pg-card-move', {
                    bubbles: true,
                    composed: true,
                    detail: { cardId, deColuna, paraColuna },
                }));
            });
        });
    }
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-kanban', PgKanban);
//# sourceMappingURL=pg-kanban.js.map